import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Sync as SyncIcon,
  Science as TestIcon
} from '@mui/icons-material';
import whatsappService from '../services/whatsappService';
import {
  WhatsAppProvider,
  ProviderBranchSettings,
  WhatsAppTemplate
} from '../types/whatsapp';

const WhatsAppProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<WhatsAppProvider[]>([]);
  const [settings, setSettings] = useState<ProviderBranchSettings[]>([]);
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [addProviderDialogOpen, setAddProviderDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<WhatsAppProvider | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    apiKey: '',
    apiSecret: '',
    accountSid: '',
    phoneNumberId: '',
    fromNumber: '',
    webhookUrl: ''
  });

  // New provider form state
  const [newProviderData, setNewProviderData] = useState({
    providerType: '',
    name: '',
    slug: '',
    description: '',
    apiEndpoint: '',
    supportsTemplates: true,
    supportsMedia: true,
    supportsBulkSending: true,
    maxBulkSize: 1000,
    rateLimitPerMinute: 60
  });

  // Provider types with their default configurations
  const providerTypes = [
    {
      value: 'interakt',
      label: 'Interakt',
      apiEndpoint: 'https://api.interakt.ai/v1',
      description: 'مزود Interakt لخدمات واتساب للأعمال',
      supportsTemplates: true,
      supportsMedia: true,
      supportsBulkSending: true,
      maxBulkSize: 1000,
      rateLimitPerMinute: 60
    },
    {
      value: 'twilio',
      label: 'Twilio',
      apiEndpoint: 'https://api.twilio.com/2010-04-01',
      description: 'مزود Twilio لخدمات واتساب',
      supportsTemplates: true,
      supportsMedia: true,
      supportsBulkSending: true,
      maxBulkSize: 500,
      rateLimitPerMinute: 80
    },
    {
      value: 'whatsapp-business',
      label: 'WhatsApp Business API',
      apiEndpoint: 'https://graph.facebook.com/v18.0',
      description: 'واجهة برمجة تطبيقات واتساب للأعمال الرسمية',
      supportsTemplates: true,
      supportsMedia: true,
      supportsBulkSending: true,
      maxBulkSize: 1000,
      rateLimitPerMinute: 80
    },
    {
      value: 'custom',
      label: 'مزود مخصص',
      apiEndpoint: '',
      description: 'مزود خدمة واتساب مخصص',
      supportsTemplates: true,
      supportsMedia: true,
      supportsBulkSending: true,
      maxBulkSize: 1000,
      rateLimitPerMinute: 60
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [providersData, settingsData, templatesData] = await Promise.all([
        whatsappService.getProviders(),
        whatsappService.getBranchSettings(),
        whatsappService.getTemplates()
      ]);
      setProviders(providersData);
      setSettings(settingsData);
      setTemplates(templatesData);
    } catch (error: any) {
      showAlert('error', error.message || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleToggleProvider = async (provider: WhatsAppProvider) => {
    try {
      await whatsappService.toggleProvider(provider._id);
      showAlert('success', `تم ${provider.isActive ? 'تعطيل' : 'تفعيل'} المزود بنجاح`);
      loadData();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل تغيير حالة المزود');
    }
  };

  const handleOpenSettings = (provider: WhatsAppProvider) => {
    setSelectedProvider(provider);
    
    // Load existing settings if available
    const existingSettings = settings.find(s => s.providerId === provider._id);
    if (existingSettings) {
      setFormData({
        apiKey: existingSettings.credentials.apiKey || '',
        apiSecret: existingSettings.credentials.apiSecret || '',
        accountSid: existingSettings.credentials.accountSid || '',
        phoneNumberId: existingSettings.credentials.phoneNumberId || '',
        fromNumber: existingSettings.credentials.fromNumber || '',
        webhookUrl: existingSettings.webhookUrl || ''
      });
    } else {
      setFormData({
        apiKey: '',
        apiSecret: '',
        accountSid: '',
        phoneNumberId: '',
        fromNumber: '',
        webhookUrl: ''
      });
    }
    
    setSettingsDialogOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsDialogOpen(false);
    setSelectedProvider(null);
    setFormData({
      apiKey: '',
      apiSecret: '',
      accountSid: '',
      phoneNumberId: '',
      fromNumber: '',
      webhookUrl: ''
    });
  };

  const handleSaveSettings = async () => {
    if (!selectedProvider) return;

    try {
      await whatsappService.saveBranchSettings({
        providerId: selectedProvider._id,
        providerSlug: selectedProvider.slug,
        isActive: true,
        credentials: {
          apiKey: formData.apiKey,
          apiSecret: formData.apiSecret,
          accountSid: formData.accountSid,
          phoneNumberId: formData.phoneNumberId,
          fromNumber: formData.fromNumber
        },
        webhookUrl: formData.webhookUrl
      });

      showAlert('success', 'تم حفظ الإعدادات بنجاح');
      handleCloseSettings();
      loadData();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل حفظ الإعدادات');
    }
  };

  const handleTestConnection = async () => {
    if (!selectedProvider) return;

    try {
      setTestingConnection(true);
      const result = await whatsappService.testConnection({
        providerId: selectedProvider._id,
        credentials: {
          apiKey: formData.apiKey,
          apiSecret: formData.apiSecret,
          accountSid: formData.accountSid,
          phoneNumberId: formData.phoneNumberId,
          fromNumber: formData.fromNumber
        }
      });

      if (result.success) {
        showAlert('success', result.message || 'الاتصال ناجح!');
      } else {
        showAlert('error', result.message || 'فشل الاتصال');
      }
    } catch (error: any) {
      showAlert('error', error.message || 'فشل اختبار الاتصال');
    } finally {
      setTestingConnection(false);
    }
  };

  // Removed handleSyncTemplates - Interakt doesn't provide templates API

  const getProviderSettings = (providerId: string) => {
    return settings.find(s => {
      // Handle both string and ObjectId comparison
      const settingsProviderId = typeof s.providerId === 'object' && s.providerId !== null
        ? (s.providerId as any)._id || (s.providerId as any).toString()
        : s.providerId;
      return settingsProviderId === providerId || settingsProviderId?.toString() === providerId;
    });
  };

  const handleProviderTypeChange = (providerType: string) => {
    const selectedType = providerTypes.find(p => p.value === providerType);
    if (selectedType) {
      setNewProviderData({
        providerType: selectedType.value,
        name: selectedType.label,
        slug: selectedType.value,
        description: selectedType.description,
        apiEndpoint: selectedType.apiEndpoint,
        supportsTemplates: selectedType.supportsTemplates,
        supportsMedia: selectedType.supportsMedia,
        supportsBulkSending: selectedType.supportsBulkSending,
        maxBulkSize: selectedType.maxBulkSize,
        rateLimitPerMinute: selectedType.rateLimitPerMinute
      });
    }
  };

  const handleDeleteProvider = async (provider: any) => {
    if (!window.confirm(`هل أنت متأكد من حذف المزود "${provider.name}"؟`)) {
      return;
    }

    try {
      await whatsappService.deleteProvider(provider._id);
      showAlert('success', 'تم حذف المزود بنجاح');
      loadData();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل حذف المزود');
    }
  };

  const handleAddProvider = async () => {
    try {
      const { providerType, ...providerData } = newProviderData;
      await whatsappService.createProvider(providerData);
      showAlert('success', 'تم إضافة المزود بنجاح');
      setAddProviderDialogOpen(false);
      setNewProviderData({
        providerType: '',
        name: '',
        slug: '',
        description: '',
        apiEndpoint: '',
        supportsTemplates: true,
        supportsMedia: true,
        supportsBulkSending: true,
        maxBulkSize: 1000,
        rateLimitPerMinute: 60
      });
      loadData();
    } catch (error: any) {
      showAlert('error', error.message || 'فشل إضافة المزود');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          إدارة مزودي واتساب
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddProviderDialogOpen(true)}
        >
          إضافة مزود
        </Button>
      </Box>

      {/* Alert */}
      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* Providers Grid */}
      <Grid container spacing={3}>
        {providers.map((provider) => {
          const providerSettings = getProviderSettings(provider._id);
          const isConfigured = !!providerSettings;
          const isActive = providerSettings?.isActive || false;

          return (
            <Grid item xs={12} md={6} lg={4} key={provider._id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {provider.name}
                      </Typography>
                      <Chip
                        label={isConfigured ? (isActive ? 'مفعل' : 'معطل') : 'غير مُعد'}
                        color={isConfigured ? (isActive ? 'success' : 'default') : 'warning'}
                        size="small"
                      />
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive}
                          onChange={() => handleToggleProvider(provider)}
                          disabled={!isConfigured}
                        />
                      }
                      label=""
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {provider.description || 'مزود خدمة واتساب'}
                  </Typography>

                  {/* Features */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      المميزات:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {provider.features.supportsTemplates && (
                        <Chip label="قوالب" size="small" variant="outlined" />
                      )}
                      {provider.features.supportsMedia && (
                        <Chip label="وسائط" size="small" variant="outlined" />
                      )}
                      {provider.features.supportsBulkSending && (
                        <Chip label="إرسال جماعي" size="small" variant="outlined" />
                      )}
                    </Box>
                  </Box>

                  {/* Statistics */}
                  {providerSettings && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        الإحصائيات:
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            المرسل: {providerSettings.statistics.totalSent}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            المُسلّم: {providerSettings.statistics.totalDelivered}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SettingsIcon />}
                      onClick={() => handleOpenSettings(provider)}
                      sx={{ flex: 1 }}
                    >
                      {isConfigured ? 'تعديل الإعدادات' : 'إعداد'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteProvider(provider)}
                      title="حذف المزود"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Templates Section - Hidden as Interakt doesn't provide templates API */}
      {false && templates.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            القوالب المتاحة ({templates.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>اسم القالب</TableCell>
                  <TableCell>الفئة</TableCell>
                  <TableCell>اللغة</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>مرات الاستخدام</TableCell>
                  <TableCell>معدل النجاح</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.slice(0, 10).map((template) => (
                  <TableRow key={template._id}>
                    <TableCell>{template.displayName}</TableCell>
                    <TableCell>
                      <Chip label={template.category} size="small" />
                    </TableCell>
                    <TableCell>{template.language}</TableCell>
                    <TableCell>
                      <Chip
                        label={template.status}
                        size="small"
                        color={template.status === 'approved' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{template.usageCount}</TableCell>
                    <TableCell>
                      {template.usageCount > 0
                        ? `${Math.round((template.successCount / template.usageCount) * 100)}%`
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={handleCloseSettings} maxWidth="sm" fullWidth>
        <DialogTitle>
          إعدادات {selectedProvider?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="API Key"
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
              fullWidth
              required
              type="password"
            />
            
            {selectedProvider?.slug === 'twilio' && (
              <>
                <TextField
                  label="Account SID"
                  value={formData.accountSid}
                  onChange={(e) => setFormData({ ...formData, accountSid: e.target.value })}
                  fullWidth
                />
                <TextField
                  label="Auth Token"
                  value={formData.apiSecret}
                  onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
                  fullWidth
                  type="password"
                />
              </>
            )}

            <TextField
              label="رقم الهاتف (اختياري)"
              value={formData.fromNumber}
              onChange={(e) => setFormData({ ...formData, fromNumber: e.target.value })}
              fullWidth
              placeholder="+966xxxxxxxxx"
            />

            <TextField
              label="Webhook URL (اختياري)"
              value={formData.webhookUrl}
              onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              fullWidth
              placeholder="https://yourdomain.com/api/webhooks/whatsapp/interakt"
            />

            {/* Test Connection - Only for providers that support it */}
            {selectedProvider?.slug !== 'interakt' && (
              <Button
                variant="outlined"
                startIcon={<TestIcon />}
                onClick={handleTestConnection}
                disabled={testingConnection || !formData.apiKey}
              >
                {testingConnection ? 'جاري الاختبار...' : 'اختبار الاتصال'}
              </Button>
            )}

            {/* Info for Interakt */}
            {selectedProvider?.slug === 'interakt' && (
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>ملاحظة:</strong> Interakt لا يوفر endpoint لاختبار الاتصال. يمكنك حفظ الإعدادات مباشرة، وسيتم التحقق من صحة API Key عند إرسال أول رسالة.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>إلغاء</Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            disabled={!formData.apiKey}
          >
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Provider Dialog */}
      <Dialog
        open={addProviderDialogOpen}
        onClose={() => setAddProviderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>إضافة مزود جديد</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>نوع المزود *</InputLabel>
              <Select
                value={newProviderData.providerType}
                onChange={(e) => handleProviderTypeChange(e.target.value)}
                label="نوع المزود *"
              >
                {providerTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {newProviderData.providerType && (
              <>
                <TextField
                  label="اسم المزود *"
                  value={newProviderData.name}
                  onChange={(e) => setNewProviderData({ ...newProviderData, name: e.target.value })}
                  fullWidth
                  placeholder="Interakt"
                />

                <TextField
                  label="المعرف (Slug) *"
                  value={newProviderData.slug}
                  onChange={(e) => setNewProviderData({ ...newProviderData, slug: e.target.value.toLowerCase() })}
                  fullWidth
                  placeholder="interakt"
                  helperText="يُستخدم في الروابط والكود (حروف صغيرة وشرطات فقط)"
                  disabled={newProviderData.providerType !== 'custom'}
                />

                <TextField
                  label="الوصف"
                  value={newProviderData.description}
                  onChange={(e) => setNewProviderData({ ...newProviderData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="مزود خدمة واتساب للأعمال"
                />

                <TextField
                  label="API Endpoint *"
                  value={newProviderData.apiEndpoint}
                  onChange={(e) => setNewProviderData({ ...newProviderData, apiEndpoint: e.target.value })}
                  fullWidth
                  placeholder="https://api.interakt.ai/v1"
                />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="الحد الأقصى للإرسال الجماعي"
                      type="number"
                      value={newProviderData.maxBulkSize}
                      onChange={(e) => setNewProviderData({ ...newProviderData, maxBulkSize: parseInt(e.target.value) })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="رسائل/دقيقة"
                      type="number"
                      value={newProviderData.rateLimitPerMinute}
                      onChange={(e) => setNewProviderData({ ...newProviderData, rateLimitPerMinute: parseInt(e.target.value) })}
                      fullWidth
                    />
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    المميزات المدعومة:
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newProviderData.supportsTemplates}
                        onChange={(e) => setNewProviderData({ ...newProviderData, supportsTemplates: e.target.checked })}
                      />
                    }
                    label="يدعم القوالب"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newProviderData.supportsMedia}
                        onChange={(e) => setNewProviderData({ ...newProviderData, supportsMedia: e.target.checked })}
                      />
                    }
                    label="يدعم الوسائط"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newProviderData.supportsBulkSending}
                        onChange={(e) => setNewProviderData({ ...newProviderData, supportsBulkSending: e.target.checked })}
                      />
                    }
                    label="يدعم الإرسال الجماعي"
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProviderDialogOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleAddProvider}
            variant="contained"
            disabled={!newProviderData.providerType || !newProviderData.name || !newProviderData.slug || !newProviderData.apiEndpoint}
          >
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppProvidersPage;